import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Box, Link, Svg, Text, Tooltip } from '@hopskipdrive/react-components';
import { Edit as EditIcon } from '@mui/icons-material';

import { Button } from '../../../../shared/components/Button';
import { Card } from '../../../../shared/components/Card';
import { CopyToClipboard } from '../../../../shared/components/CopyToClipboard';
import { StartIcon } from '../../../../shared/components/Icons';
import { colorTokens } from '../../../../shared/components/variables';
import { formatInTimezone } from '../../../../shared/utils/helpers/date';
import { RIDE_STATES } from '../../../constants/rideStates';
import { useRideOverviewQuery } from '../../../hooks/useRideOverviewQuery';
import { InfoNavContent } from '..';

import { EditTimestampModal } from './EditTimestampModal/EditTimestampModal';
import { SummaryOverview } from './SummaryOverview';
import { TimelineOverview } from './TimelineOverview';

interface RideOverviewTabProps {
  rideState: string;
  hasAllRidersRemoved: boolean;
  isCarePartnerRide?: boolean;
}

export const RideOverviewTab: FC<RideOverviewTabProps> = ({
  rideState,
  hasAllRidersRemoved,
  isCarePartnerRide = false,
}) => {
  const { rideSlug } = useParams();
  const { data: rideOverviewDetails, isFetching } = useRideOverviewQuery({ rideSlug: rideSlug || '' });
  const isPartOfSeries = rideOverviewDetails?.data?.tripTemplateId;
  const [openEditTimestampModal, setOpenEditTimestampModal] = useState(false);

  return (
    <InfoNavContent>
      <Card>
        <MainHeading
          rideSlug={rideSlug || ''}
          rideState={rideState}
          setOpenEditTimestampModal={setOpenEditTimestampModal}
          isFetching={isFetching}
          isCarePartnerRide={isCarePartnerRide}
        />
        {rideOverviewDetails?.data?.cancellationReason && (
          <Alert
            icon={false}
            severity="error"
            variant="outlined"
            message={`Canceled on ${formatInTimezone(
              rideOverviewDetails?.data?.canceledAt || new Date(),
              rideOverviewDetails?.data?.timeZone,
              'MM/dd/yyyy hh:mm aa zzz'
            )} with reason "${rideOverviewDetails?.data?.cancellationReason}"`}
            style={{ width: '100%' }}
          />
        )}
        {hasAllRidersRemoved && (
          <Alert
            severity="warning"
            variant="filled"
            message="All the riders have been removed."
            style={{ width: '100%', fontSize: '20px', fontWeight: 500 }}
          />
        )}
        {isPartOfSeries && (
          <IsSeriesLabel adminRepeatingRidesLink={rideOverviewDetails?.data?.adminRepeatingRidesLink || ''} />
        )}
        {!hasAllRidersRemoved && (
          <Box display="flex" gap="20px" sx={{ width: '100%' }}>
            <TimelineOverview isFetching={isFetching} rideOverviewDetails={rideOverviewDetails?.data} />
          </Box>
        )}
        <SummaryOverview isFetching={isFetching} summaryDetails={rideOverviewDetails?.data?.rideSummary} />
        {rideOverviewDetails?.data?.adminTripReplayLink && (
          <Button
            color="tonal variant"
            size="small"
            startIcon={<StartIcon />}
            disabled={isFetching}
            onClick={() => window.open(rideOverviewDetails?.data?.adminTripReplayLink, '_blank')}
          >
            Replay Ride
          </Button>
        )}
        {openEditTimestampModal && (
          <EditTimestampModal
            rideOverviewDetails={rideOverviewDetails?.data}
            rideSlug={rideSlug}
            onClose={() => {
              setOpenEditTimestampModal(false);
            }}
          />
        )}
      </Card>
    </InfoNavContent>
  );
};

interface MainHeadingProps {
  rideSlug: string;
  rideState: string;
  setOpenEditTimestampModal: (open: boolean) => void;
  isFetching: boolean;
  isCarePartnerRide?: boolean;
}

const MainHeading: FC<MainHeadingProps> = ({
  rideSlug,
  rideState,
  setOpenEditTimestampModal,
  isFetching,
  isCarePartnerRide = false,
}) => {
  const shouldDisableEditTimestamp = [...RIDE_STATES.upcoming, ...RIDE_STATES.canceled].includes(rideState);

  return (
    <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
      <Box display="flex" flexDirection="row" alignItems="center" gap="12px">
        <Svg icon="timeline" style={{ color: colorTokens.primary.main }} />
        <Text variant="h6" style={{ color: colorTokens.primary.main }}>
          Ride Overview
        </Text>
        <CopyToClipboard toastMessage="Slug Copied" text={rideSlug} variant="h6" color={colorTokens.text.secondary} />
      </Box>
      <Tooltip title="Available in Admin" placement="top" disableHoverListener={!isCarePartnerRide}>
        <div>
          <Button
            color="tonal variant"
            size="small"
            startIcon={<EditIcon />}
            disabled={shouldDisableEditTimestamp || isFetching || isCarePartnerRide}
            onClick={() => setOpenEditTimestampModal(true)}
          >
            Edit Timestamp
          </Button>
        </div>
      </Tooltip>
    </Box>
  );
};

interface IsSeriesLabelProps {
  adminRepeatingRidesLink: string;
}

const IsSeriesLabel: FC<IsSeriesLabelProps> = ({ adminRepeatingRidesLink }) => {
  return (
    <Box display="flex" flexDirection="row" alignItems="center" gap="12px" pl="13px">
      <Svg icon="repeat" style={{ color: colorTokens.text.secondary }} />
      <Link target="_blank" underline="none" href={adminRepeatingRidesLink}>
        <Text variant="h6" style={{ color: colorTokens.text.secondary }}>
          This ride is part of a series
        </Text>
      </Link>
    </Box>
  );
};
